import React, { useState } from 'react';
import { Alert, Loader } from '@mantine/core';
import { useParams } from 'react-router-dom';
import { useDashboardPage } from '../hooks/useDashboardPage';
import { DashboardHeader } from '../components/DashboardHeader/DashboardHeader';
import { FilterDialog } from '../components/FilterDialog/FilterDialog';
import { TopSummarySection } from '../components/TopSummarySection/TopSummarySection';
import { BreakdownRowModal, SalesSummaryModalContext } from '../components/BreakdownRowModal/BreakdownRowModal';
import { ApprovalRequestsSection } from '../components/ApprovalRequestsSection/ApprovalRequestsSection';
import { CancellationsSection } from '../components/CancellationsSection/CancellationsSection';
import { WaiterIssuesSection } from '../components/WaiterIssuesSection/WaiterIssuesSection';
import { CategoriesSection } from '../components/CategoriesSection/CategoriesSection';
import { BestSellingSection } from '../components/BestSellingSection/BestSellingSection';
import { MostCancelledSection } from '../components/MostCancelledSection/MostCancelledSection';
import { SalesSection } from '../components/SalesSection/SalesSection';
import { PaymentMethodSection } from '../components/PaymentMethodSection/PaymentMethodSection';
import { PaymentsSection } from '../components/PaymentsSection/PaymentsSection';
import { RefundsSection } from '../components/RefundsSection/RefundsSection';
import { SalesGraphSection } from '../components/SalesGraphSection/SalesGraphSection';
import { BumpTimesSection } from '../components/BumpTimesSection/BumpTimesSection';
import { LoadPreviousShiftSection } from '../components/LoadPreviousShiftSection/LoadPreviousShiftSection';
import { AssistantPanel } from '../components/AssistantPanel/AssistantPanel';
import { useAuth } from '../../../contexts/AuthProvider';
import classes from './dashboard-page.module.scss';

const DASHBOARD_THEME_STORAGE_KEY = 'dashboard-light-mode';
const DASHBOARD_THEME_ATTR = 'data-dashboard-theme';
const DASHBOARD_THEME_LIGHT = 'light';
const DASHBOARD_THEME_DARK = 'dark';

const readInitialLightMode = (): boolean => {
  try {
    const stored = localStorage.getItem(DASHBOARD_THEME_STORAGE_KEY);
    return stored === null ? false : stored === 'true';
  } catch {
    return false;
  }
};

export const DashboardPage: React.FC = () => {
  const dashboard = useDashboardPage();
  const { tenantId } = useParams();
  const { user } = useAuth();
  const [selectedSalesSummary, setSelectedSalesSummary] = useState<SalesSummaryModalContext | null>(null);
  const [isLightMode, setIsLightMode] = useState<boolean>(readInitialLightMode);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const shouldShowPartialDataAlert = Boolean(dashboard.error) && user?.role !== 'admin';
  const shouldShowReportNotice = dashboard.reportDataFailureSections.length > 0;
  const affectedReportSectionsText = dashboard.reportDataFailureSections.join(' ,');

  React.useEffect(() => {
    document.documentElement.setAttribute(
      DASHBOARD_THEME_ATTR,
      isLightMode ? DASHBOARD_THEME_LIGHT : DASHBOARD_THEME_DARK,
    );
  }, [isLightMode]);

  React.useEffect(() => () => {
    document.documentElement.removeAttribute(DASHBOARD_THEME_ATTR);
  }, []);

  const toggleThemeMode = () => {
    setIsLightMode((previous) => {
      const next = !previous;
      try {
        localStorage.setItem(DASHBOARD_THEME_STORAGE_KEY, String(next));
      } catch {
        // storage may fail in private mode
      }
      return next;
    });
  };

  const renderDashboardBlock = (block: typeof dashboard.dashboardBlocks[number]) => (
    <React.Fragment key={block.key}>
      <TopSummarySection
        collapsedSections={dashboard.collapsedSections}
        toggleSection={dashboard.toggleSection}
        trendSignClass={block.trendSignClass}
        sameDaysComparison={block.sameDaysComparison}
        comparePct={block.comparePct}
        totalsCards={block.totalsCards}
        topKpiRows={block.topKpiRows}
        onTotalsCardClick={(card) => {
          dashboard.setTotalsMode(card.key);
          setSelectedSalesSummary({
            selectedCard: card,
            report: block.report,
            orders: block.liveOrders,
            closedOrderDinersByCode: block.closedOrderDinersByCode,
            payments: block.payments,
          });
        }}
      />

      <ApprovalRequestsSection
        collapsedSections={dashboard.collapsedSections}
        toggleSection={dashboard.toggleSection}
        approvalMode={dashboard.approvalMode}
        setApprovalMode={dashboard.setApprovalMode}
        loadMoreApprovalRows={dashboard.loadMoreApprovalRows}
        approvalRows={block.approvalRows}
        approvalDetailsById={dashboard.approvalDetailsById}
        managerOptions={dashboard.managerOptions}
        selectedApproverName={dashboard.selectedApproverName}
        setSelectedApproverName={dashboard.setSelectedApproverName}
        approvalsActionBusy={dashboard.approvalsActionBusy}
        approvalsActionError={dashboard.approvalsActionError}
        runApprovalDecision={dashboard.runApprovalDecision}
        approvalScopeDateText={dashboard.headerDateText}
        loadApprovalExportSource={dashboard.loadApprovalExportSource}
        approvalContextLabel={dashboard.filterMode === 'shift'
          ? (block.session?.sessionName?.trim() || (block.session?.sessionId ? `משמרת ${block.session.sessionId}` : dashboard.headerDateText))
          : dashboard.headerDateText}
      />

      <SalesSection
        collapsedSections={dashboard.collapsedSections}
        toggleSection={dashboard.toggleSection}
        report={block.report}
        selectedRevenue={block.selectedRevenue}
      />

      <PaymentMethodSection
        collapsedSections={dashboard.collapsedSections}
        toggleSection={dashboard.toggleSection}
        paymentByMethod={block.paymentByMethod}
        activeSessions={block.activeSessions}
        payments={block.payments}
        reportsBySessionId={block.reportsBySessionId}
      />

      <SalesGraphSection
        collapsedSections={dashboard.collapsedSections}
        toggleSection={dashboard.toggleSection}
        graphRows={block.graphRows}
      />

      <PaymentsSection
        collapsedSections={dashboard.collapsedSections}
        toggleSection={dashboard.toggleSection}
        paymentByMethod={block.paymentByMethod}
        report={block.report}
      />

      <RefundsSection
        collapsedSections={dashboard.collapsedSections}
        toggleSection={dashboard.toggleSection}
        payments={block.payments}
      />

      <CancellationsSection
        collapsedSections={dashboard.collapsedSections}
        toggleSection={dashboard.toggleSection}
        tenantId={block.activeSessions[0]?.tenantId ?? ''}
        sessionIds={block.activeSessions.map((session) => session.sessionId)}
        canonicalItemsCount={block.report?.canceledItemsCount}
        canonicalItemsAmount={block.report?.canceledItemsAmount}
      />

      <WaiterIssuesSection
        collapsedSections={dashboard.collapsedSections}
        toggleSection={dashboard.toggleSection}
        waiterActionBreakdown={block.waiterActionBreakdown}
        waiterActionRows={block.waiterActionRows}
        approvalDetailsById={dashboard.approvalDetailsById}
      />

      <CategoriesSection
        collapsedSections={dashboard.collapsedSections}
        toggleSection={dashboard.toggleSection}
        categorySales={block.categorySales}
      />

      <BestSellingSection
        collapsedSections={dashboard.collapsedSections}
        toggleSection={dashboard.toggleSection}
        bestSellingProducts={block.bestSellingProducts}
      />

      <MostCancelledSection
        collapsedSections={dashboard.collapsedSections}
        toggleSection={dashboard.toggleSection}
        mostCanceledProducts={block.mostCanceledProducts}
      />

      <BumpTimesSection
        collapsedSections={dashboard.collapsedSections}
        toggleSection={dashboard.toggleSection}
        bumpTimesSummary={block.bumpTimesSummary}
      />
    </React.Fragment>
  );

  return (
    <main
      className={[classes.page, isLightMode ? classes.pageLight : ''].filter(Boolean).join(' ')}
      dir="rtl"
    >
      {dashboard.loading ? (
        <div className={classes.loadingOverlay} role="status" aria-label="טוען נתוני לוח בקרה" data-testid="dashboard-loading-overlay">
          <div className={classes.loadingOverlayContent}>
            <Loader size={72} color="white" />
            <span className={classes.loadingOverlayText}>טוען נתונים</span>
          </div>
        </div>
      ) : null}
      <div className={classes.container}>
        <DashboardHeader
          headerDateText={dashboard.headerDateText}
          setIsFilterDialogOpen={dashboard.setIsFilterDialogOpen}
          anchorMonthSalesExTips={dashboard.anchorMonthSalesExTips}
          anchorMonthLabel={dashboard.anchorMonthLabel}
          loading={dashboard.loading}
          isLightMode={isLightMode}
          onToggleThemeMode={toggleThemeMode}
          onOpenAssistant={tenantId ? () => setIsAssistantOpen(true) : undefined}
        />

{shouldShowPartialDataAlert ? <Alert color="orange">טעינת חלק מהנתונים נכשלה. מוצגים נתונים חלקיים.</Alert> : null}

        <section className={classes.sections}>
          {dashboard.dashboardBlocks.map((block, index) => (
            <React.Fragment key={block.key}>
              {dashboard.filterMode === 'shift' && index > 0 ? (
                <div className={classes.sessionSeparator}>
                  <span className={classes.sessionSeparatorChip}>משמרת קודמת</span>
                </div>
              ) : null}
              {renderDashboardBlock(block)}
            </React.Fragment>
          ))}
          <LoadPreviousShiftSection
            filterMode={dashboard.filterMode}
            loadedSessionIds={dashboard.loadedSessionIds}
            sessions={dashboard.sessions}
            onLoadPreviousShift={dashboard.onLoadPreviousShift}
          />
        </section>

        {shouldShowReportNotice ? (
          <Alert className={classes.reportNotice} mt="md">
            חלק ממדדי הדוחות לא נטענו. אזורים מושפעים: {affectedReportSectionsText}.
          </Alert>
        ) : null}
      </div>

      <FilterDialog
        isFilterDialogOpen={dashboard.isFilterDialogOpen}
        setIsFilterDialogOpen={dashboard.setIsFilterDialogOpen}
        filterMode={dashboard.filterMode}
        setFilterMode={dashboard.setFilterMode}
        tenantSessionOptions={dashboard.tenantSessionOptions}
        sessions={dashboard.sessions}
        canonicalSessionId={dashboard.canonicalSessionId}
        canonicalSessionRevenue={dashboard.canonicalSessionRevenue}
        pendingSessionId={dashboard.pendingSessionId}
        setPendingSessionId={dashboard.setPendingSessionId}
        rangeStart={dashboard.rangeStart}
        rangeEnd={dashboard.rangeEnd}
        setDateRange={dashboard.setDateRange}
        setLoadedSessionIds={dashboard.setLoadedSessionIds}
      />

      <BreakdownRowModal
        selectedSummary={selectedSalesSummary}
        onClose={() => setSelectedSalesSummary(null)}
      />
      <AssistantPanel tenantId={tenantId ?? ''} opened={isAssistantOpen} onClose={() => setIsAssistantOpen(false)} />

    </main>
  );
};
